import { DebugLogger } from '@affine/debug';
import type {
  StoreClient,
  WorkerInitOptions,
} from '@affine/nbstore/worker/client';
import { captureException, captureMessage } from '@sentry/react';
import { Entity, LiveData } from '@toeverything/infra';
import { firstValueFrom, Observable } from 'rxjs';

import type { CorruptionReporterService } from '../../cloud';
import type { FeatureFlagService } from '../../feature-flag';
import type { NbstoreService } from '../../storage';
import { WorkspaceEngineBeforeStart } from '../events';
import {
  assessBrowserStoragePressure,
  type BrowserStoragePressure,
} from '../services/browser-storage-pressure';
import {
  IntegrityCheckService,
  type IntegrityCheckSummary,
} from '../services/integrity-check';
import type { WorkspaceService } from '../services/workspace';

// Dev-only diagnostics. Off by default; enable via `?debug` (DebugLogger /
// @affine/debug). Prod builds strip console.* via webpack pure_funcs anyway.
const logger = new DebugLogger('affine:workspace:engine');

// Report the persistent-storage outcome to Sentry at most once per client
// session so the "% of users running on evictable storage" metric is not
// skewed by per-workspace-switch re-requests (requestPersistentStorage runs on
// every WorkspaceEngine start).
let persistOutcomeReported = false;

export class WorkspaceEngine extends Entity<{
  isSharedMode?: boolean;
  engineWorkerInitOptions: WorkerInitOptions;
}> {
  client?: StoreClient;
  started = false;

  /**
   * LiveData for integrity check result.
   * null = not started, undefined = in progress, IntegrityCheckSummary = completed
   */
  readonly integrityCheckResult$ = new LiveData<
    IntegrityCheckSummary | null | undefined
  >(null);

  /**
   * Origin-scoped browser storage headroom.
   * null = unsupported/unavailable, undefined = check in progress.
   */
  readonly browserStoragePressure$ = new LiveData<
    BrowserStoragePressure | null | undefined
  >(null);

  /**
   * IntegrityCheckService instance (available after start)
   */
  private integrityCheckService?: IntegrityCheckService;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly nbstoreService: NbstoreService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly corruptionReporter?: CorruptionReporterService
  ) {
    super();
  }

  get doc() {
    if (!this.client) {
      throw new Error('Engine is not initialized');
    }
    return this.client.docFrontend;
  }

  get blob() {
    if (!this.client) {
      throw new Error('Engine is not initialized');
    }
    return this.client.blobFrontend;
  }

  get indexer() {
    if (!this.client) {
      throw new Error('Engine is not initialized');
    }
    return this.client.indexerFrontend;
  }

  get awareness() {
    if (!this.client) {
      throw new Error('Engine is not initialized');
    }
    return this.client.awarenessFrontend;
  }

  /**
   * sl-j143.5: Observable for permission change events.
   * Returns null if engine not started (safe for optional subscription).
   */
  get permissionsChanged$(): Observable<{
    eventId?: string;
    workspaceId: string;
    changeType: string;
    timestamp: number;
    permissionGraphVersion?: number;
    refreshMode?: 'scoped' | 'lazy' | 'full';
    affectedFolderIds?: string[];
    affectedDocIds?: string[];
    docPlacementChanges?: Array<{
      docId: string;
      fromFolderId: string | null;
      toFolderId: string;
    }>;
    folderPlacementChanges?: Array<{
      folderId: string;
      fromParentId: string | null;
      toParentId: string;
    }>;
    revokedFolderIds?: string[];
  }> | null {
    return this.client?.docSync.permissionsChanged$ ?? null;
  }

  /**
   * Observable for workspace meta change events.
   * Returns null if engine not started (safe for optional subscription).
   */
  get workspaceMetaChanged$(): Observable<{
    workspaceId: string;
    changeType: string;
    docId?: string | null;
    timestamp: number;
    source: string;
  }> | null {
    return this.client?.docSync.workspaceMetaChanged$ ?? null;
  }

  /**
   * sl-lkdb.3.2: Observable for recovery completed events.
   * Returns null if engine not started (safe for optional subscription).
   */
  get recoveryCompleted$(): Observable<{
    workspaceId: string;
    newRecoveryVersion: number;
    timestamp: number;
  }> | null {
    return this.client?.docSync.recoveryCompleted$ ?? null;
  }

  /**
   * sl-lv2w.5: Observable for doc size warning events (soft limit reached).
   * Returns null if engine not started (safe for optional subscription).
   */
  get docSizeWarning$(): Observable<{
    docId: string;
    spaceId: string;
    sizeBytes: number;
    warnBytes: number;
    limitBytes: number;
  }> | null {
    return this.client?.docSync.docSizeWarning$ ?? null;
  }

  /**
   * sl-lv2w.5: Observable for doc size hard limit events.
   * Returns null if engine not started (safe for optional subscription).
   */
  get docSizeHardLimit$(): Observable<{
    docId: string;
    spaceId: string;
    sizeBytes: number;
    limitBytes: number;
  }> | null {
    return this.client?.docSync.docSizeHardLimit$ ?? null;
  }

  /**
   * sl-00ro: Observable for embedding doc status events.
   * Returns null if engine not started (safe for optional subscription).
   */
  get embeddingDocStatus$(): Observable<{
    workspaceId: string;
    docId: string;
    status: 'indexing' | 'indexed' | 'failed';
    chunksIndexed?: number;
    chunksTotal?: number;
    timestamp: number;
  }> | null {
    return this.client?.docSync.embeddingDocStatus$ ?? null;
  }

  /**
   * sl-00ro: Observable for embedding workspace progress events.
   * Returns null if engine not started (safe for optional subscription).
   */
  get embeddingWorkspaceProgress$(): Observable<{
    workspaceId: string;
    docsIndexed: number;
    docsTotal: number;
    timestamp: number;
  }> | null {
    return this.client?.docSync.embeddingWorkspaceProgress$ ?? null;
  }

  /**
   * Observable for knowledge graph update events.
   */
  get kgUpdated$(): Observable<{
    workspaceId: string;
    entityCount: number;
    relationCount: number;
    timestamp: number;
  }> | null {
    return this.client?.docSync.kgUpdated$ ?? null;
  }

  start() {
    if (this.started) {
      throw new Error('Engine is already started');
    }
    this.started = true;

    const baseStoreKey =
      (this.props.isSharedMode ? 'shared:' : '') +
      `workspace:${this.workspaceService.workspace.flavour}:${this.workspaceService.workspace.id}`;
    const identityKey = this.props.engineWorkerInitOptions.identityKey;
    const { store, dispose } = this.nbstoreService.openStore(
      identityKey ? `${baseStoreKey}:${identityKey}` : baseStoreKey,
      this.props.engineWorkerInitOptions
    );
    if (
      this.featureFlagService.flags.enable_battery_save_mode.value &&
      this.workspaceService.workspace.flavour !== 'local'
    ) {
      store.enableBatterySaveMode().catch((err: unknown) => {
        console.error('error enabling battery save mode', err);
      });
    }
    this.client = store;
    this.disposables.push(dispose);

    // Wire up corruption reporting - ALWAYS set callbacks for logging, even without reporter
    const workspaceId = this.workspaceService.workspace.id;

    // Report corruption from DocFrontend (local applyUpdate errors)
    store.docFrontend.setOnCorruptionDetected(
      (info: {
        docId: string;
        operation: string;
        error: Error;
        binaryData?: Uint8Array;
      }) => {
        logger.debug('[WorkspaceEngine] DocFrontend corruption detected:', {
          docId: info.docId,
          operation: info.operation,
          error: info.error.message,
        });
        if (this.corruptionReporter) {
          this.corruptionReporter.reportYjsError({
            workspaceId,
            docId: info.docId,
            error: info.error,
            operation: info.operation as
              | 'applyUpdate'
              | 'encodeStateVector'
              | 'encodeStateAsUpdate'
              | 'other',
            binaryData: info.binaryData,
          });
        }
      }
    );

    // Report corruption from DocSync (server rejections, encode errors, diff failures)
    const corruptionSubscription = store.docSync.corruption$.subscribe(
      (event: {
        docId: string;
        errorType: string;
        errorMessage: string;
        operation: string;
        binLength?: number;
        binPreview?: number[];
      }) => {
        console.warn('[WorkspaceEngine] corruption$ event:', event);
        if (this.corruptionReporter) {
          this.corruptionReporter.report({
            workspaceId,
            docId: event.docId,
            errorType: event.errorType,
            errorMessage: event.errorMessage,
            operation: event.operation,
            binLength: event.binLength ?? 0,
            // Convert number[] back to Uint8Array (Worker serialization converts Uint8Array to number[])
            binPreview: event.binPreview
              ? new Uint8Array(event.binPreview)
              : undefined,
          });
        }
      }
    );
    this.disposables.push(() => corruptionSubscription.unsubscribe());

    // sl-affine-uhr: Handle doc compacted events from server
    // When a doc is compacted (e.g. strip history), clear local IDB state
    // so the sync engine pulls the clean snapshot from the server.
    if ('compacted$' in store.docSync && store.docSync.compacted$) {
      const compactedSubscription = store.docSync.compacted$.subscribe(
        (event: unknown) => {
          if (!event || typeof event !== 'object' || !('docId' in event)) {
            return;
          }
          const e = event as {
            docId: string;
            originalSizeBytes?: number;
            newSizeBytes?: number;
            reductionPercent?: number;
          };
          logger.debug(
            `[WorkspaceEngine] doc:compacted: ${e.docId} ` +
              `${e.originalSizeBytes ?? '?'} -> ${e.newSizeBytes ?? '?'} bytes ` +
              `(-${e.reductionPercent ?? '?'}%)`
          );

          // Clear local IDB state (not remote!) so sync pulls fresh snapshot.
          // resetLocalDoc clears only local storage; deleteDoc cascades to remote.
          const resetFn =
            'resetLocalDoc' in store &&
            typeof store.resetLocalDoc === 'function'
              ? store.resetLocalDoc.bind(store)
              : null;

          if (resetFn) {
            resetFn(e.docId)
              .then(() => {
                logger.debug(
                  `[WorkspaceEngine] Local state cleared for ${e.docId}, triggering re-sync`
                );
                this.doc.addPriority(e.docId, 200);
              })
              .catch((err: unknown) => {
                console.error(
                  `[WorkspaceEngine] Failed to reset local doc ${e.docId}:`,
                  err
                );
              });
          } else {
            // Fallback: just bump priority (local state will be cleaned on next refresh)
            logger.debug(
              `[WorkspaceEngine] resetLocalDoc not available, bumping sync priority for ${e.docId}`
            );
            this.doc.addPriority(e.docId, 200);
          }
        }
      );
      this.disposables.push(() => compactedSubscription.unsubscribe());
    }

    this.eventBus.emit(WorkspaceEngineBeforeStart, this);

    const rootDoc = this.workspaceService.workspace.docCollection.doc;
    // Cloud root metadata is served through the permission-filtered metadata
    // APIs. Raw Y.Doc sync is intentionally denied by the backend.
    if (this.workspaceService.workspace.flavour === 'local') {
      this.doc.addDoc(rootDoc.guid, 'local-workspace-root');
      this.doc.addPriority(rootDoc.guid, 100);
    }
    // Indexer always needs root doc for local search (no server round-trip)
    this.indexer.addPriority(rootDoc.guid, 100);
    this.doc.start();
    this.disposables.push(() => this.doc.stop());

    // sl-hn2: Expose debug functions globally for sync debugging
    const self = this;
    (globalThis as Record<string, unknown>).__AFFINE_SYNC_DEBUG__ = {
      getDocSyncState: () => {
        // Subscribe to get current value
        let currentState: unknown = null;
        const sub = self.client?.docSync.state$.subscribe((state: unknown) => {
          currentState = state;
        });
        sub?.unsubscribe();
        return currentState;
      },
      getDocState: async (docId: string) => {
        const state$ = self.client?.docSync.docState$(docId);
        return {
          aggregate: state$ ? await firstValueFrom(state$) : null,
          peers: await self.client?.docSync.debugDocState(docId),
        };
      },
      forceSync: async (docId: string) => {
        logger.debug(`[DEBUG] Force sync requested for ${docId}`);
        const doc = self.workspaceService.workspace.docCollection.getDoc(docId);
        if (doc) {
          self.doc.addPriority(doc.id, 100);
          logger.debug(`[DEBUG] Added priority for ${docId}`);
        } else {
          logger.debug(`[DEBUG] Doc ${docId} not found in docCollection`);
        }
      },
      waitForSynced: async (docId?: string) => {
        logger.debug(`[DEBUG] Waiting for sync: ${docId || 'all'}`);
        await self.client?.docSync.waitForSynced(docId);
        logger.debug(`[DEBUG] Sync complete: ${docId || 'all'}`);
      },
      // sl-sync-flush: Flush all pending updates and wait for backend ACK
      flushAndWaitForSynced: async (docId: string, abort?: AbortSignal) => {
        logger.debug(`[DEBUG] Flush and wait for sync: ${docId}`);
        await self.client?.docSync.flushAndWaitForSynced(docId, abort);
        logger.debug(`[DEBUG] Flush complete: ${docId}`);
      },
      listDocs: () => {
        const docs = Array.from(
          self.workspaceService.workspace.docCollection.docs.values()
        );
        return docs.map(d => ({ id: d.id, loaded: d.loaded }));
      },
      workspaceId: workspaceId,
    };

    // sl-canvas2-8.24.22: Request persistent storage to prevent browser eviction
    // Safari deletes IndexedDB after 7 days of inactivity, this prevents it
    this.requestPersistentStorage();

    // Warn before IndexedDB starts rejecting writes. This remains advisory;
    // workspace boot and document reads must never depend on this estimate.
    this.checkBrowserStoragePressure();

    // sl-canvas2-8.24.21: Background integrity check at startup
    this.runBackgroundIntegrityCheck(store, workspaceId);
  }

  /**
   * Run integrity check in background after startup.
   * Uses quick sample check first, then full check if issues found.
   */
  private runBackgroundIntegrityCheck(store: StoreClient, workspaceId: string) {
    this.integrityCheckService = new IntegrityCheckService(
      store,
      this.corruptionReporter,
      workspaceId
    );

    // Mark as in progress
    this.integrityCheckResult$.next(undefined);

    // Run check in background (don't await - non-blocking)
    // Use setTimeout to ensure it runs after initial render
    setTimeout(() => {
      this.integrityCheckService
        ?.quickCheck(10) // Sample of 10 docs for fast startup
        .then(summary => {
          this.integrityCheckResult$.next(summary);

          if (summary.corrupt > 0) {
            console.warn(
              `[WorkspaceEngine] Integrity check found ${summary.corrupt} corrupt docs!`,
              summary.corruptDocs.map(d => d.docId)
            );
            // Full check in background if quick check found issues
            return this.integrityCheckService?.checkAll({
              delayMs: 50, // Throttle to not block main thread
              onCorruptDoc: result => {
                console.warn(
                  `[WorkspaceEngine] Corrupt doc: ${result.docId}`,
                  result.error
                );
              },
            });
          }
          return undefined;
        })
        .then(fullSummary => {
          if (fullSummary) {
            this.integrityCheckResult$.next(fullSummary);
          }
        })
        .catch(err => {
          console.error('[WorkspaceEngine] Integrity check failed:', err);
        });
    }, 2000); // Delay 2s to let workspace load first
  }

  /**
   * sl-canvas2-8.24.22: Request persistent storage to prevent browser eviction.
   * Safari deletes IndexedDB after 7 days of inactivity without this.
   * Chrome/Safari auto-approve based on site engagement, no user prompt.
   */
  private requestPersistentStorage(): void {
    if (typeof navigator === 'undefined') return;
    if (!navigator.storage?.persist) return;

    navigator.storage
      .persist()
      .then(persisted => {
        if (!persisted) {
          logger.debug(
            '[WorkspaceEngine] Persistent storage denied — browser may evict IndexedDB after inactivity'
          );
          // Telemetry only (no user-facing UI): lets ops measure how many
          // clients run on evictable storage and are exposed to browser
          // eviction (e.g. Safari wiping IndexedDB after ~7 days of inactivity).
          if (!persistOutcomeReported) {
            persistOutcomeReported = true;
            captureMessage('Persistent storage denied by browser', {
              level: 'warning',
              tags: { feature: 'persistent-storage', outcome: 'denied' },
            });
          }
        }
      })
      .catch(err => {
        // Non-critical, just log
        console.warn(
          '[WorkspaceEngine] Failed to request persistent storage:',
          err
        );
        if (!persistOutcomeReported) {
          persistOutcomeReported = true;
          captureException(err, {
            tags: { feature: 'persistent-storage', outcome: 'error' },
          });
        }
      });
  }

  /**
   * Read the browser's origin quota without blocking workspace startup.
   * Websites cannot read the device's actual free disk space, so this signal
   * deliberately stays origin-scoped and fail-open when unsupported.
   */
  private checkBrowserStoragePressure(): void {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
      this.browserStoragePressure$.next(null);
      return;
    }

    this.browserStoragePressure$.next(undefined);
    navigator.storage
      .estimate()
      .then(estimate => {
        this.browserStoragePressure$.next(
          assessBrowserStoragePressure(estimate)
        );
      })
      .catch(err => {
        this.browserStoragePressure$.next(null);
        logger.debug(
          '[WorkspaceEngine] Browser storage estimate unavailable',
          err
        );
      });
  }

  /**
   * Manually trigger a full integrity check.
   */
  async runFullIntegrityCheck(): Promise<IntegrityCheckSummary | null> {
    if (!this.integrityCheckService) {
      console.warn('[WorkspaceEngine] Integrity check service not available');
      return null;
    }

    this.integrityCheckResult$.next(undefined); // Mark as in progress

    const summary = await this.integrityCheckService.checkAll({
      onProgress: (checked, total) => {
        logger.debug(`[IntegrityCheck] Progress: ${checked}/${total}`);
      },
    });

    this.integrityCheckResult$.next(summary);
    return summary;
  }
}
